import type {
  StorefrontBuyerLoginStartRequest,
  StorefrontBuyerLoginStartResponse,
  StorefrontBuyerLoginVerifyRequest,
  StorefrontBuyerLoginVerifyResponse,
  StorefrontBuyerPasswordResetCompleteRequest,
  StorefrontBuyerPasswordResetCompleteResponse,
  StorefrontBuyerPasswordResetStartRequest,
  StorefrontBuyerPasswordResetStartResponse,
  StorefrontBuyerRegistrationRequest,
  StorefrontBuyerRegistrationStartResponse,
  StorefrontBuyerRegistrationVerifyRequest,
  StorefrontBuyerRegistrationVerifyResponse,
  StorefrontBuyerSessionResponse,
  StorefrontCatalogResponse,
  StorefrontOrderListResponse,
  StorefrontOrderSubmitRequest,
  StorefrontOrderSubmitResponse,
  StorefrontPublicBootstrapResponse,
} from '@stokmerter/contracts';

export class StorefrontApiError extends Error {
  constructor(readonly code: string, message: string) { super(message); }
}

async function requestJson<T>(path: string, init: RequestInit): Promise<T> {
  const response = await fetch(`/api${path}`, {
    credentials: 'same-origin',
    headers: { Accept: 'application/json', ...(init.body ? { 'Content-Type': 'application/json' } : {}) },
    ...init,
  });
  let body: unknown;
  try { body = await response.json(); } catch { throw new StorefrontApiError('INVALID_RESPONSE', 'Sunucu yanıtı alınamadı.'); }
  if (!response.ok) {
    const error = body && typeof body === 'object' && 'error' in body
      ? (body as { error?: { code?: unknown; message?: unknown } }).error
      : undefined;
    throw new StorefrontApiError(
      typeof error?.code === 'string' ? error.code : 'REQUEST_FAILED',
      typeof error?.message === 'string' ? error.message : 'İşlem tamamlanamadı.',
    );
  }
  return body as T;
}

function post<T>(path: string, body?: unknown): Promise<T> {
  return requestJson(path, { method: 'POST', ...(body === undefined ? {} : { body: JSON.stringify(body) }) });
}

export function loadPublicStorefront(signal?: AbortSignal): Promise<StorefrontPublicBootstrapResponse> {
  return requestJson('/v1/public/storefront', { method: 'GET', signal });
}

export function registerBuyer(input: StorefrontBuyerRegistrationRequest) {
  return post<StorefrontBuyerRegistrationStartResponse>('/v1/buyer/registration/start', input);
}
export function verifyBuyerRegistration(input: StorefrontBuyerRegistrationVerifyRequest) {
  return post<StorefrontBuyerRegistrationVerifyResponse>('/v1/buyer/registration/verify', input);
}
export function startBuyerLogin(input: StorefrontBuyerLoginStartRequest) {
  return post<StorefrontBuyerLoginStartResponse>('/v1/buyer/login/start', input);
}
export function verifyBuyerLogin(input: StorefrontBuyerLoginVerifyRequest) {
  return post<StorefrontBuyerLoginVerifyResponse>('/v1/buyer/login/verify', input);
}
export function loadBuyerSession(signal?: AbortSignal) {
  return requestJson<StorefrontBuyerSessionResponse>('/v1/buyer/session', { method: 'GET', signal });
}
export function loadBuyerCatalog(cursor?: string, signal?: AbortSignal) {
  const query = cursor ? `?cursor=${encodeURIComponent(cursor)}` : '';
  return requestJson<StorefrontCatalogResponse>(`/v1/buyer/catalog${query}`, { method: 'GET', signal });
}
export function loadBuyerOrders(signal?: AbortSignal) {
  return requestJson<StorefrontOrderListResponse>('/v1/buyer/orders', { method: 'GET', signal });
}
export function submitBuyerOrder(input: StorefrontOrderSubmitRequest) {
  return post<StorefrontOrderSubmitResponse>('/v1/buyer/orders', input);
}

export async function downloadBuyerCatalogImage(imageId: string, signal?: AbortSignal) {
  const response = await fetch(`/api/v1/buyer/catalog/images/${encodeURIComponent(imageId)}`, {
    credentials: 'same-origin',
    headers: { Accept: 'application/octet-stream' },
    signal,
  });
  if (!response.ok) {
    let message = 'Ürün görseli alınamadı.';
    try {
      const body = await response.json() as { error?: { message?: unknown } };
      if (typeof body.error?.message === 'string') message = body.error.message;
    } catch { /* Binary endpoint may return a non-JSON intermediary error. */ }
    throw new StorefrontApiError('CATALOG_IMAGE_FAILED', message);
  }
  return new Uint8Array(await response.arrayBuffer());
}
export function logoutBuyer() { return post<{ ok: true }>('/v1/buyer/logout'); }
export function startBuyerPasswordReset(input: StorefrontBuyerPasswordResetStartRequest) {
  return post<StorefrontBuyerPasswordResetStartResponse>('/v1/buyer/password-reset/start', input);
}
export function completeBuyerPasswordReset(input: StorefrontBuyerPasswordResetCompleteRequest) {
  return post<StorefrontBuyerPasswordResetCompleteResponse>('/v1/buyer/password-reset/complete', input);
}
